import {
  computed,
  DestroyRef,
  Injectable,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

import { AuthStore } from '@auth/store/auth.store';
import { ROLES } from '@auth/models/roles.model';

export interface NavigationRoute {
  path: string;
  label: string;
  icon: string;
  requiredRole: string;
}

@Injectable({
  providedIn: 'root',
})
export class NavigationService {
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly authStore = inject(AuthStore);

  /**
   * URL used for active-route styling. Updated in a microtask after
   * `NavigationEnd` so it does not change during the same change-detection pass
   * as child views (avoids NG0100 under zoneless).
   */
  private readonly stableUrl = signal(this.router.url);

  constructor() {
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        const next = this.router.url;
        window.queueMicrotask(() => {
          if (next !== this.stableUrl()) {
            this.stableUrl.set(next);
          }
        });
      });
  }

  readonly isAuthenticated = this.authStore.isAuthenticated;

  readonly canViewSayp = computed(() =>
    this.authStore.hasRole(ROLES.SAYP_VIEW)
  );
  readonly canEnrollCustomers = computed(() =>
    this.authStore.hasRole(ROLES.SAYP_ENROLL)
  );
  readonly canConfigureSayp = computed(() =>
    this.authStore.hasRole(ROLES.SAYP_CONFIGURE)
  );
  readonly canManageStatements = computed(() =>
    this.authStore.hasRole(ROLES.SAYP_STATEMENTS)
  );
  readonly canFlaggedMatchSayp = computed(() =>
    this.authStore.hasRole(ROLES.SAYP_FLAGGED_MATCH)
  );
  readonly canViewReports = computed(() =>
    this.authStore.hasRole(ROLES.SAYP_REPORTS)
  );

  readonly allSaypRoutes: NavigationRoute[] = [
    {
      path: '/save-as-you-pay/landing',
      label: 'Home',
      icon: 'home-outline',
      requiredRole: ROLES.SAYP_VIEW,
    },
    {
      path: '/save-as-you-pay/flagged-transactions',
      label: 'Flagged Transactions',
      icon: 'list-outline',
      requiredRole: ROLES.SAYP_FLAGGED_MATCH,
    },
    {
      path: '/save-as-you-pay/enroll-customer',
      label: 'Enroll Customer',
      icon: 'person-add-outline',
      requiredRole: ROLES.SAYP_ENROLL,
    },
    {
      path: '/save-as-you-pay/statement-manager',
      label: 'Statements',
      icon: 'document-text-outline',
      requiredRole: ROLES.SAYP_STATEMENTS,
    },
    {
      path: '/save-as-you-pay/reports',
      label: 'Reports',
      icon: 'bar-chart-outline',
      requiredRole: ROLES.SAYP_REPORTS,
    },
    {
      path: '/save-as-you-pay/program-settings',
      label: 'Configuration',
      icon: 'settings-outline',
      requiredRole: ROLES.SAYP_CONFIGURE,
    },
  ];

  readonly saypRoutes = computed(() => {
    if (!this.authStore.isAuthenticated()) {
      return [];
    }
    return this.allSaypRoutes.filter(route => {
      switch (route.requiredRole) {
        case ROLES.SAYP_VIEW:
          return this.canViewSayp();
        case ROLES.SAYP_ENROLL:
          return this.canEnrollCustomers();
        case ROLES.SAYP_STATEMENTS:
          return this.canManageStatements();
        case ROLES.SAYP_CONFIGURE:
          return this.canConfigureSayp();
        case ROLES.SAYP_FLAGGED_MATCH:
          return this.canFlaggedMatchSayp();
        case ROLES.SAYP_REPORTS:
          return this.canViewReports();
        default:
          return false;
      }
    });
  });

  navigateTo(path: string): void {
    void this.router.navigate([path]);
  }

  signIn(): void {
    this.authStore.login();
  }

  signOut(): void {
    this.authStore.logout();
  }

  isRouteActive(routePath: string): boolean {
    return this.stableUrl() === routePath;
  }

  isSaypRouteActive(): boolean {
    return this.stableUrl().startsWith('/save-as-you-pay');
  }
}
